
import { Component, Vue } from "vue-property-decorator";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import { mapState } from "vuex";
import Package from "@/models/Subscriptions/SubscriptionPackages/Package";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import SystemConfigurationRepository from "@/utils/API/SystemConfigurationRepository";
import { shorten } from "@/utils/Helpers";

import vTable from "@/components/vTable.vue";
import vToggleSwitch from "@/components/vToggleSwitch.vue";
import vToggleSwitchSelfHandled from "@/components/vToggleSwitchSelfHandled.vue";
import vModal from "@/components/vModal.vue";
import draggable from "vuedraggable";
import { SubscriptionRecurrenceType } from "@/models/Subscriptions/SubscriptionRecurrenceType";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import AddOn from "@/models/Subscriptions/AddOns/AddOn";

@Component({
  components: {
    vTable,
    vModal,
    vToggleSwitch,
    vToggleSwitchSelfHandled,
    draggable,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class Packages extends Vue {
  localizationState: any;

  addOnType: any = AddOnType;

  //////////
  // Data
  //////////

  nutrientSources: any[] = [];
  packages: Package[] = [];
  gridsLoading: boolean = false;
  selectedCycle: SubscriptionRecurrenceType = SubscriptionRecurrenceType.Monthly;
  showActive: boolean = true;
  removingPackagesModel: { packages: number[]; modal: any } = {
    packages: [],
    modal: {
      isOpen: false,
    },
  };

  subscriptionSettingsModel: any = {
    trialDays: 0,
    defaultPackageId: 0,
  };

  creatingPackageModel: any = {
    modal: {
      isOpen: false,
    },
    package: {
      name: "",
      cycle: SubscriptionRecurrenceType.Monthly,
      price: 0,
      externalId: "",
      isHighlighted: false,

      unlimitedRecipes: false,
      unlimitedDishes: false,
      unlimitedLabels: false,
      recipesLimit: 0,
      dishesLimit: 0,
      labelsLimit: 0,

      nutrientSources: [],
      addOns: [],
      freeUserLicenses: 0,
      userLicensesRanges: [],
      isEnterprise: false,
    },
  };

  updatingPackageModel: any = {
    modal: {
      isOpen: false,
    },
    package: {
      id: null,
      name: "",
      cycle: SubscriptionRecurrenceType.Monthly,
      price: 0,
      externalId: "",
      isHighlighted: false,

      unlimitedRecipes: false,
      unlimitedDishes: false,
      unlimitedLabels: false,
      recipesLimit: 0,
      dishesLimit: 0,
      labelsLimit: 0,

      nutrientSources: [],
      addOns: [],
      freeUserLicenses: 0,
      userLicensesRanges: [],
      isEnterprise: false,
    },
  };

  //////////
  // Computed
  //////////

  get translations() {
    return this.localizationState.translations;
  }

  get gridHeaders() {
    return [
      {
        name: this.translations.get("packages_table_header_name"),
        key: "Name",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("package_id"),
        key: "Id",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("package_status"),
        key: "IsActive",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("created_on"),
        key: "CreatedOn",
        searchable: false,
        searchString: "",
        sortable: true,
      },

      {
        name: this.translations.get("packages_table_header_database"),
        key: "AvailableNutrientSource",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("packages_table_header_external_monthly"),
        key: "ExternalId",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("packages_table_header_price"),
        key: "Price",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      // {
      //   name: this.translations.get("packages_table_header_cost_user"),
      //   key: "CostPerUser",
      //   searchable: false,
      //   searchString: "",
      //   sortable: true
      // },
      // {
      //   name: this.translations.get("packages_table_header_nousers"),
      //   key: "UsersLimit",
      //   searchable: false,
      //   searchString: "",
      //   sortable: true,
      // },
      {
        name: this.translations.get("packages_table_header_norecipes"),
        key: "RecipesLimit",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("packages_table_header_nodishes"),
        key: "DishesLimit",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("packages_table_header_nolabels"),
        key: "LabelsLimit",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      // {
      //   name: this.translations.get("packages_table_header_trialdays"),
      //   key: "TrialPeriodInDays",
      //   searchable: false,
      //   searchString: "",
      //   sortable: true,
      // },
      // {
      //   name: this.translations.get("packages_table_header_default"),
      //   key: "IsDefault",
      //   searchable: false,
      //   searchString: "",
      //   sortable: false,
      // },
      // {
      //   name: this.translations.get("packages_table_header_hasaccess"),
      //   key: "HasWebAppAccess",
      //   searchable: false,
      //   searchString: "",
      //   sortable: false,
      // },
      {
        name: this.translations.get("packages_modal_highlight"),
        key: "isHighlighted",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("packages_table_header_legacy"),
        key: "IsLegacy",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("packages_table_header_nosubscribers"),
        key: "SubscriptionsCount",
        searchable: false,
        searchString: "",
        sortable: false,
      },
    ];
  }

  get monthlyPackages() {
    return this.packages.filter((x: any) => x.cycle == 0);
  }

  get yearlyPackages() {
    return this.packages.filter((x: any) => x.cycle == 1);
  }

  get removingConfirmationModalTitle() {
    return this.translations.get(
      "packages_delete_prompt_message",
      this.removingPackagesModel.packages.length
    );
  }

  //////////
  // Methods
  //////////

  openCreatingPackageModal() {
    this.creatingPackageModel.modal.isOpen = true;
    this.creatingPackageModel.package.name = "";
    this.creatingPackageModel.package.externalId = "";
    this.creatingPackageModel.package.cycle =
      SubscriptionRecurrenceType.Monthly;
    this.creatingPackageModel.package.price = 0;
    this.creatingPackageModel.package.unlimitedRecipes = false;
    this.creatingPackageModel.package.unlimitedDishes = false;
    this.creatingPackageModel.package.unlimitedLabels = false;
    this.creatingPackageModel.package.recipesLimit = 0;
    this.creatingPackageModel.package.dishesLimit = 0;
    this.creatingPackageModel.package.labelsLimit = 0;

    this.creatingPackageModel.package.nutrientSources.length = 0;
    this.creatingPackageModel.package.isHighlighted = false;

    this.creatingPackageModel.package.freeUserLicenses = 0;
    this.creatingPackageModel.package.userLicensesRanges = [
      { fromRange: 0, toRange: null, userLicensePrice: null },
    ];

    this.creatingPackageModel.package.addOns = Object.keys(AddOnType)
      .filter((key: any) => isNaN(Number(AddOnType[key])))
      .map((x) => {
        return {
          addOn: x,
          name: this.translations.get(`addon-${x}`),
          isFree: true,
          price: 0,
          freeTrialPeriodInMonths: 0,
          isActive: x != this.addOnType.FieldEagle ? true : false
        };
      });
  }

  toggleCycle() {
    this.selectedCycle =
      this.selectedCycle === SubscriptionRecurrenceType.Monthly
        ? SubscriptionRecurrenceType.Yearly
        : SubscriptionRecurrenceType.Monthly;
  }

  toggleActive() {
    this.showActive = !this.showActive;
  }

  transformPackagesAsRows() {
    let packages =
      this.selectedCycle === SubscriptionRecurrenceType.Monthly
        ? this.monthlyPackages
        : this.yearlyPackages;

    packages = packages.filter((p) => p.isActive === this.showActive);

    return packages.map((pck: Package) => {
      return {
        meta: {
          id: pck.id,
          link: pck.name
        },
        e1: pck.id,
        e2: (pck.isActive)
          ? {
            component: "div",
            props: { class: "label label-success text-center" },
            html: this.translations.get("active"),
          }
          : {
            component: "div",
            props: { class: "label label-danger text-center" },
            html: this.translations.get("inactive"),
          },
        e3: this.$moment(pck.createdOn).format('DD MMM YYYY'),
        e4: {
          component: "ul",
          props: {
            style:
              "list-style: none; padding-inline-start: 0px; margin-bottom: 0",
          },
          html: this.getNutrientSourcesDisplayForGrid(
            pck.availableNutrientSources
          ),
        },
        e5: pck.externalId,
        e6: pck.price,
        e7: pck.recipesLimit
          ? pck.recipesLimit
          : this.translations.get("unlimited"),
        e8: pck.dishesLimit
          ? pck.dishesLimit
          : this.translations.get("unlimited"),
        e9: pck.labelsLimit
          ? pck.labelsLimit
          : this.translations.get("unlimited"),

        e10: {
          component: "vToggleSwitchSelfHandled",
          props: {
            id: pck.id,
            item: pck,
            isChecked: pck.isHighlighted,
          },
          events: {
            "vToggleSwitchSelfHandled:changed": (item: any) => {
              let isHighlighted = !item.isHighlighted;
              SubscriptionRepository.highlightSubscriptionPackage(
                item.id,
                isHighlighted
              )
                .then(() => {
                  item.isHighlighted = isHighlighted;
                })
                .catch((error) => {
                  let message = "";
                  if (error.response) {
                    message =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    message = this.translations.get("network_error");
                  }
                });
            },
          },
        },
        e11: {
          component: "vToggleSwitchSelfHandled",
          props: {
            id: pck.id,
            item: pck,
            isChecked: pck.isLegacy,
          },
          events: {
            "vToggleSwitchSelfHandled:changed": (item: Package) => {
              let isLegacy = !item.isLegacy;
              SubscriptionRepository.flagSubscriptionPackageAsLegacy(
                item.id,
                isLegacy
              )
                .then(() => {
                  item.isLegacy = isLegacy;
                })
                .catch((error) => {
                  let message = "";
                  if (error.response) {
                    message =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    message = this.translations.get("network_error");
                  }
                });
            },
          },
        },
        e12: pck.subscriptionsCount,
      };
    });
  }

  getSubscriptionSystemSettings() {
    SubscriptionRepository.getSubscriptionSystemSettings()
      .then((response) => {
        this.subscriptionSettingsModel.trialDays =
          response.data.result.trialDays;
        this.subscriptionSettingsModel.defaultPackageId =
          response.data.result.trialSubscriptionPackageId;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
      });
  }

  setSubscriptionSystemSettings() {
    let settings = {
      trialDays: this.subscriptionSettingsModel.trialDays,
      trialSubscriptionPackageId:
        this.subscriptionSettingsModel.defaultPackageId,
    };
    SubscriptionRepository.setSubscriptionSystemSettings(settings)
      .then(() => {
        this.$snotify.success(
          this.translations.get("packages_succ_msg_update")
        );
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
      });
  }

  getNutrientSourcesDisplayForGrid(nutrientSources: any[]) {
    let display = "";
    nutrientSources.map((source: any) => {
      display +=
        "<li class='badge badge-success badge-pill text-left' style='width: 100%; margin-bottom: 3px;'><i class='fa fa-database'></i> " +
        shorten(this.translations.get(`nutrient_source_${source}`)) +
        "</li>";
    });
    return display;
  }

  loadPackages() {
    this.gridsLoading = true;

    SubscriptionRepository.displaySubscriptionPackages(true)
      .then((response) => {
        this.packages = response.data.result;
        this.gridsLoading = false;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
        this.gridsLoading = false;
      });
  }

  loadNutrientSources() {
    SystemConfigurationRepository.fetchNutrientSourcesList()
      .then((response) => {
        this.nutrientSources = response.data.result;
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("network_error");
        }
        this.$snotify.error(message);
      });
  }

  openEditModal(packageId: number) {
    let pck = this.packages.find((x) => x.id == packageId);
    if (pck) {
      this.updatingPackageModel.package.id = pck.id;
      this.updatingPackageModel.package.name = pck.name;
      this.updatingPackageModel.package.externalId = pck.externalId;
      this.updatingPackageModel.package.cycle = pck.cycle;
      this.updatingPackageModel.package.price = pck.price;
      this.updatingPackageModel.package.unlimitedRecipes =
        pck.recipesLimit == null ? true : false;
      this.updatingPackageModel.package.unlimitedDishes =
        pck.dishesLimit == null ? true : false;
      this.updatingPackageModel.package.unlimitedLabels =
        pck.labelsLimit == null ? true : false;
      this.updatingPackageModel.package.recipesLimit = pck.recipesLimit;
      this.updatingPackageModel.package.dishesLimit = pck.dishesLimit;
      this.updatingPackageModel.package.labelsLimit = pck.labelsLimit;
      this.updatingPackageModel.package.isHighlighted = pck.isHighlighted;
      this.updatingPackageModel.package.freeUserLicenses = pck.freeUserLicenses;
      this.updatingPackageModel.package.nutrientSources =
        pck.availableNutrientSources;
      this.updatingPackageModel.package.userLicensePricingRanges =
        pck.userLicensePricingRanges;
      this.updatingPackageModel.package.isEnterprise =
        pck.isEnterprise;

      if (
        this.updatingPackageModel.package.userLicensePricingRanges.length == 0
      ) {
        return this.updatingPackageModel.package.userLicensePricingRanges.push({
          from: 0,
          to: null,
          price: null,
        });
      }

      this.updatingPackageModel.package.addOns = pck.addOns.map((addOn: any) => {
        if (addOn) {
          return {
            addOn: addOn.addOn,
            name: this.translations.get(`addon-${addOn.addOn}`),
            isFree: addOn.price == null,
            price: addOn.price != null ? addOn.price : 0,
            freeTrialPeriodInMonths:
              addOn.price != null ? addOn.freeTrialPeriodInMonths : 0,
            isActive: addOn.isActive
          };
        }
      });

      this.updatingPackageModel.modal.isOpen = true;
    }
  }

  submitUpdate() {
    this.$validator.validateAll().then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("packages_toastr_updating"),
          () =>
            new Promise((resolve, reject) => {
              let updatingPackage = {
                id: this.updatingPackageModel.package.id,
                name: this.updatingPackageModel.package.name,
                price: this.updatingPackageModel.package.price,

                recipesLimit: this.updatingPackageModel.package.unlimitedRecipes
                  ? null
                  : this.updatingPackageModel.package.recipesLimit,
                dishesLimit: this.updatingPackageModel.package.unlimitedDishes
                  ? null
                  : this.updatingPackageModel.package.dishesLimit,
                labelsLimit: this.updatingPackageModel.package.unlimitedLabels
                  ? null
                  : this.updatingPackageModel.package.labelsLimit,
                nutrientSources:
                  this.updatingPackageModel.package.nutrientSources,
                freeUserLicenses:
                  this.updatingPackageModel.package.freeUserLicenses,
                userLicensePricingRanges:
                  this.updatingPackageModel.package.userLicensePricingRanges.map(
                    (x: any) => {
                      return {
                        from: x.from,
                        to: x.to,
                        price: x.price,
                        subscriptionPackageId:
                          this.updatingPackageModel.package.id,
                      };
                    }
                  ),
                addOns: this.updatingPackageModel.package.addOns.map(
                  (x: any) => {
                    return {
                      addOn: x.addOn,
                      price: x.isFree ? null : x.price,
                      freeTrialPeriodInMonths: x.isFree
                        ? 0
                        : x.freeTrialPeriodInMonths,
                      isActive: x.isActive
                    };
                  }
                ),
                isEnterprise:
                  this.updatingPackageModel.package.isEnterprise,
              };

              SubscriptionRepository.updateSubscriptionPackage(updatingPackage)
                .then(() => {
                  resolve({
                    body: this.translations.get(
                      "packages_toastr_updated",
                      updatingPackage.name
                    ),
                  });

                  this.loadPackages();
                  this.getSubscriptionSystemSettings();
                  this.updatingPackageModel.modal.isOpen = false;
                })
                .catch((error) => {
                  let message = "";
                  if (error.response) {
                    message =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    message = this.translations.get("network_error");
                  }
                  reject({ body: message });
                });
            }),
          {
            closeOnClick: true,
            timeout: 7000,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  submitNew() {
    this.$validator.validateAll().then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("packages_toastr_adding"),
          () =>
            new Promise((resolve, reject) => {
              let creatingPackage: any = {
                name: this.creatingPackageModel.package.name,
                externalId: this.creatingPackageModel.package.externalId,
                price: this.creatingPackageModel.package.price,
                isHighlighted: this.creatingPackageModel.package.isHighlighted,
                cycle: this.creatingPackageModel.package.cycle,

                recipesLimit: this.creatingPackageModel.package.unlimitedRecipes
                  ? null
                  : this.creatingPackageModel.package.recipesLimit,
                dishesLimit: this.creatingPackageModel.package.unlimitedDishes
                  ? null
                  : this.creatingPackageModel.package.dishesLimit,
                labelsLimit: this.creatingPackageModel.package.unlimitedLabels
                  ? null
                  : this.creatingPackageModel.package.labelsLimit,

                nutrientSources:
                  this.creatingPackageModel.package.nutrientSources,

                freeUserLicenses:
                  this.creatingPackageModel.package.freeUserLicenses,
                userLicensePricingRanges:
                  this.creatingPackageModel.package.userLicensesRanges.map(
                    (x: any) => {
                      return {
                        from: x.fromRange,
                        to: x.toRange,
                        price: x.userLicensePrice,
                      };
                    }
                  ),

                addOns: this.creatingPackageModel.package.addOns.map(
                  (x: any) => {
                    return {
                      addOn: x.addOn,
                      price: x.isFree ? null : x.price,
                      freeTrialPeriodInMonths: x.isFree
                        ? 0
                        : x.freeTrialPeriodInMonths,
                      isActive: x.isActive
                    };
                  }
                ),
                isEnterprise:
                  this.creatingPackageModel.package.isEnterprise,
              };

              if(!creatingPackage.isEnterprise)
                creatingPackage.addOns = creatingPackage.addOns.filter(( addOn: AddOn ) => {
                  return addOn.addOn !== AddOnType.FieldEagle;
                });

              SubscriptionRepository.createSubscriptionPackage(creatingPackage)
                .then(() => {
                  resolve({
                    body: this.translations.get(
                      "packages_toastr_added",
                      creatingPackage.name
                    ),
                  });

                  this.loadPackages();
                  this.creatingPackageModel.modal.isOpen = false;
                })
                .catch((error) => {
                  let message = "";
                  if (error.response) {
                    message =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    message = this.translations.get("network_error");
                  }
                  reject({ body: message });
                });
            }),
          {
            closeOnClick: true,
            timeout: 7000,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  onEnterpriseCheckboxChange() {
    if (this.creatingPackageModel.package.isEnterprise) {
      this.creatingPackageModel.package.unlimitedRecipes = true;
      this.creatingPackageModel.package.unlimitedDishes = true;
      this.creatingPackageModel.package.unlimitedLabels = true;
      this.creatingPackageModel.package.freeUserLicenses = 3;
      this.creatingPackageModel.package.nutrientSources = [0, 1, 2];

      this.creatingPackageModel.package.addOns = Object.keys(AddOnType)
        .filter((key: any) => isNaN(Number(AddOnType[key])))
        .map((x) => {
          return {
            addOn: x,
            name: this.translations.get(`addon-${x}`),
            isFree: true,
            price: 0,
            freeTrialPeriodInMonths: 0,
            isActive: x != this.addOnType.FieldEagle ? true : false
          };
        });
    }
    else {
      this.creatingPackageModel.package.unlimitedRecipes = false;
      this.creatingPackageModel.package.unlimitedDishes = false;
      this.creatingPackageModel.package.unlimitedLabels = false;
      this.creatingPackageModel.package.freeUserLicenses = 0;
      this.creatingPackageModel.package.nutrientSources = [];

      this.creatingPackageModel.package.addOns = Object.keys(AddOnType)
        .filter((key: any) => isNaN(Number(AddOnType[key])))
        .map((x) => {
          return {
            addOn: x,
            name: this.translations.get(`addon-${x}`),
            isFree: true,
            price: 0,
            freeTrialPeriodInMonths: 0,
            isActive: x != this.addOnType.FieldEagle ? true : false
          };
        });
    }
  }

  openRemovingModal(packageIds: number[]) {
    this.removingPackagesModel.packages = packageIds;
    this.removingPackagesModel.modal.isOpen = true;
  }

  removePackages() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get(
        "packages_toastr_deleting",
        this.removingPackagesModel.packages.length
      ),
      () =>
        new Promise((resolve, reject) => {
          SubscriptionRepository.deletePackages({
            subscriptionPackages: this.removingPackagesModel.packages,
          })
            .then(() => {
              resolve({
                body: this.translations.get(
                  "packages_toastr_deleted",
                  this.removingPackagesModel.packages.length
                ),
              });
              this.removingPackagesModel.packages.length = 0;
              this.removingPackagesModel.modal.isOpen = false;
              this.loadPackages();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  getEmptyUserLicenseRange() {
    return {
      rangeFrom: 0,
      rangeTo: 0,
      userLicensePrice: 0,
    };
  }

  // Add new user license row in create package modal
  addNewUserLicenseRow() {
    let lastUserLicense =
      this.creatingPackageModel.package.userLicensesRanges[
        this.creatingPackageModel.package.userLicensesRanges.length - 1
      ];
    let fromUL = parseInt(lastUserLicense.toRange) + 1;
    this.creatingPackageModel.package.userLicensesRanges.push({
      fromRange: fromUL,
      toRange: null,
      userLicensePrice: null,
    });
  }

  // Remove user license row in create package modal
  removeUserLicenseRow(index: any) {
    this.creatingPackageModel.package.userLicensesRanges.splice(index, 1);
  }

  // Add new user license row in update package modal
  addNewUserLicenseRowInEditModal() {
    let lastUserLicense =
      this.updatingPackageModel.package.userLicensePricingRanges[
        this.updatingPackageModel.package.userLicensePricingRanges.length - 1
      ];
    let fromUL = parseInt(lastUserLicense.to) + 1;
    this.updatingPackageModel.package.userLicensePricingRanges.push({
      from: fromUL,
      to: null,
      price: null,
    });
  }

  // Remove user license row in update package modal
  removeUserLicenseRowInEditModal(index: any) {
    this.updatingPackageModel.package.userLicensePricingRanges.splice(index, 1);
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["packages", "addons", "common"],
    });

    this.getSubscriptionSystemSettings();
    this.loadPackages();
    this.loadNutrientSources();

    document.title = "Packages | MenuSano";
  }
}
export default Packages;
